import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  BarChart3, 
  RefreshCw, 
  ChevronDown, 
  ChevronUp,
  TrendingUp,
  AlertTriangle,
  Target,
  Copy,
  Check,
  Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import { useApi } from '../../hooks/useApi';

export default function ExecutiveClarityBrief() {
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [copied, setCopied] = useState(false);
  const api = useApi();

  const generateBrief = async (forceRefresh = false) => {
    setLoading(true);
    try {
      const data = await api.get(
        `/sales-intelligence/executive-brief?force_refresh=${forceRefresh}`
      );
      
      if (data.status === 'generating') {
        toast.info('Brief is being generated, please wait...');
        return;
      }
      
      if (data.brief?.error) {
        toast.error(data.brief.error);
        return;
      }
      
      setBrief(data);
      if (data.cached) {
        toast.success('Loaded cached brief');
      } else {
        toast.success('Executive Clarity Brief generated');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to generate brief');
    } finally {
      setLoading(false);
    }
  };

  const copyBrief = () => {
    if (!brief?.brief) return;
    
    const b = brief.brief;
    const text = `
EXECUTIVE CLARITY BRIEF
Generated: ${new Date(brief.generated_at).toLocaleString()}

EXECUTIVE SUMMARY
${b.executive_summary}

CURRENT SITUATION
${b.current_situation}

STRATEGIC INSIGHT
${b.strategic_insight}

OPPORTUNITY
${b.opportunity}

RISK
${b.risk}

STRATEGIC FOCUS
${b.strategic_focus?.map(f => `• ${f}`).join('\n')}

EXPECTED OUTCOME
${b.expected_outcome}
    `.trim();
    
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Brief copied to clipboard');
  };

  const formatCurrency = (value) => {
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
    return `$${value?.toFixed(0) || 0}`;
  };

  if (!brief) {
    return (
      <Card className="bg-white/80 backdrop-blur border-[#E8E6E1]">
        <CardContent className="p-6">
          <div className="text-center space-y-4">
            <div className="w-16 h-16 mx-auto rounded-full bg-[#B89B5E]/10 flex items-center justify-center">
              <BarChart3 className="h-8 w-8 text-[#B89B5E]" />
            </div>
            <div>
              <h3 className="font-medium text-[#1A1A1A]">Executive Clarity Brief</h3>
              <p className="text-sm text-muted-foreground mt-1">
                CEO-level strategic intelligence from your pipeline data
              </p>
            </div>
            <Button 
              onClick={() => generateBrief()} 
              disabled={loading}
              className="bg-[#1A1A1A] hover:bg-[#2A2A2A] text-white"
              data-testid="generate-exec-brief-btn"
            >
              {loading ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Sparkles className="h-4 w-4 mr-2" />
              )}
              Generate Executive Brief
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  const b = brief.brief;
  const stats = brief.pipeline_stats;

  return (
    <Card className="bg-white/80 backdrop-blur border-[#E8E6E1]" data-testid="executive-clarity-brief">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-[#B89B5E]/10">
              <BarChart3 className="h-5 w-5 text-[#B89B5E]" />
            </div>
            <div>
              <CardTitle className="text-lg">Executive Clarity Brief</CardTitle>
              <p className="text-xs text-muted-foreground">
                {brief.cached ? 'Cached' : 'Generated'} {new Date(brief.generated_at).toLocaleString()}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm"
              onClick={copyBrief}
            >
              {copied ? <Check className="h-3 w-3 mr-1" /> : <Copy className="h-3 w-3 mr-1" />}
              Copy
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => generateBrief(true)}
              disabled={loading}
            >
              <RefreshCw className={`h-3 w-3 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </CardHeader>
      
      {expanded && (
        <CardContent className="space-y-5">
          {/* Executive Summary */}
          <div className="p-4 rounded-lg bg-gradient-to-r from-[#B89B5E]/10 to-transparent border border-[#B89B5E]/20">
            <Badge className="mb-2 bg-[#B89B5E] text-white">Executive Summary</Badge>
            <p className="text-sm text-[#1A1A1A]">{b.executive_summary}</p>
          </div>

          {/* Key Metrics Grid */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="p-3 rounded-lg bg-[#FAF9F7] border border-[#E8E6E1] text-center">
                <p className="text-xs text-muted-foreground">Pipeline Value</p>
                <p className="text-lg font-semibold text-[#1A1A1A]">
                  {formatCurrency(stats.total_pipeline_value)}
                </p>
              </div>
              <div className="p-3 rounded-lg bg-[#FAF9F7] border border-[#E8E6E1] text-center">
                <p className="text-xs text-muted-foreground">Weighted</p>
                <p className="text-lg font-semibold text-[#B89B5E]">
                  {formatCurrency(stats.weighted_pipeline)}
                </p>
              </div>
              <div className="p-3 rounded-lg bg-[#FAF9F7] border border-[#E8E6E1] text-center">
                <p className="text-xs text-muted-foreground">Active Opps</p>
                <p className="text-lg font-semibold text-[#1A1A1A]">
                  {stats.total_active_opportunities}
                </p>
              </div>
              <div className="p-3 rounded-lg bg-[#FAF9F7] border border-[#E8E6E1] text-center">
                <p className="text-xs text-muted-foreground">In Motion</p>
                <p className="text-lg font-semibold text-emerald-600">
                  {stats.opportunities_in_motion}
                </p>
              </div>
            </div>
          )}

          {/* Current Situation */}
          <div>
            <h4 className="text-sm font-medium text-[#1A1A1A] mb-2">Current Situation</h4>
            <p className="text-sm text-[#1A1A1A]/80">{b.current_situation}</p>
          </div>

          {/* Strategic Insight */}
          <div className="p-4 rounded-lg bg-[#FAF9F7] border border-[#E8E6E1]">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="h-4 w-4 text-[#B89B5E]" />
              <h4 className="text-sm font-medium text-[#1A1A1A]">Strategic Insight</h4>
            </div>
            <p className="text-sm text-[#1A1A1A]/80">{b.strategic_insight}</p>
          </div>

          {/* Opportunity & Risk */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="p-4 rounded-lg bg-emerald-50/50 border border-emerald-100">
              <div className="flex items-center gap-2 mb-2">
                <Target className="h-4 w-4 text-emerald-600" />
                <h4 className="text-sm font-medium text-emerald-700">Opportunity</h4>
              </div>
              <p className="text-sm text-[#1A1A1A]/80">{b.opportunity}</p>
            </div>
            <div className="p-4 rounded-lg bg-amber-50/50 border border-amber-100">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <h4 className="text-sm font-medium text-amber-700">Risk</h4>
              </div>
              <p className="text-sm text-[#1A1A1A]/80">{b.risk}</p>
            </div>
          </div>

          {/* Strategic Focus */}
          {b.strategic_focus?.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-[#1A1A1A] mb-2">Strategic Focus (Next 30 Days)</h4>
              <ul className="space-y-2">
                {b.strategic_focus.map((focus, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[#B89B5E]/10 text-[#B89B5E] flex items-center justify-center text-xs font-medium">
                      {i + 1}
                    </span>
                    <span className="text-[#1A1A1A]/80">{focus}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Expected Outcome */}
          <div className="p-4 rounded-lg bg-[#1A1A1A] text-white">
            <h4 className="text-sm font-medium text-[#B89B5E] mb-2">Expected Outcome</h4>
            <p className="text-sm text-white/90">{b.expected_outcome}</p>
          </div>
        </CardContent>
      )}
    </Card>
  );
}
